import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "./Logo";
import {
  LayoutDashboard, Shield, FileText, Calculator,
  AlertCircle, LogOut, Menu, X, Home, ScrollText, Sparkles
} from "lucide-react";

const NAV = [
  { to: "/setores", icon: Home, label: "Setores" },
  { to: "/compliance", icon: Shield, label: "Compliance" },
  { to: "/compliance/ocorrencias", icon: AlertCircle, label: "Ocorrências" },
  { to: "/documentos", icon: FileText, label: "Documentos" },
  { to: "/orcamentos", icon: Calculator, label: "Orçamentos" },
];

export function Shell({ children }) {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-[#0A0B1A]">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-[#B28F4B]/15 bg-[#0A0B1A] sticky top-0 h-screen">
        <div className="p-6 border-b border-[#B28F4B]/15">
          <Logo />
        </div>
        <nav className="flex-1 py-4 px-3 overflow-y-auto">
          {NAV.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/setores"}
              data-testid={`nav-${item.to.replace(/\//g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 my-0.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? "bg-[#1F2350] text-[#B28F4B] border-l-2 border-[#B28F4B]"
                    : "text-[#A1A4C7] hover:bg-[#131532] hover:text-white border-l-2 border-transparent"
                }`
              }
            >
              <item.icon size={17} strokeWidth={1.6} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[#B28F4B]/15">
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#B28F4B] to-[#8b6f3a] flex items-center justify-center text-[#1A1D42] font-bold text-sm">
              {user?.nome?.charAt(0) || "?"}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-white text-sm font-semibold truncate">{user?.nome}</div>
              <div className="text-[#B28F4B] text-[10px] uppercase tracking-widest font-bold">{user?.nivel}</div>
            </div>
          </div>
          <button
            onClick={doLogout}
            data-testid="btn-logout"
            className="w-full flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-[#A1A4C7] hover:bg-red-500/10 hover:text-red-400 transition-colors"
          >
            <LogOut size={15} strokeWidth={1.6} />
            Sair
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-30 flex items-center justify-between px-4 py-3 bg-[#0A0B1A]/95 backdrop-blur-xl border-b border-[#B28F4B]/15">
        <Logo size="sm" />
        <button
          onClick={() => setOpen(!open)}
          data-testid="btn-mobile-menu"
          className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#131532] text-[#B28F4B]"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden fixed inset-0 z-20 pt-16 bg-[#0A0B1A]/98 backdrop-blur-2xl">
          <nav className="px-6 py-6 flex flex-col gap-2">
            {NAV.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-4 rounded-xl text-base font-medium ${
                    isActive ? "bg-[#1F2350] text-[#B28F4B]" : "text-[#A1A4C7] bg-[#131532]"
                  }`
                }
              >
                <item.icon size={20} />
                {item.label}
              </NavLink>
            ))}
            <button
              onClick={doLogout}
              className="mt-4 flex items-center gap-3 px-5 py-4 rounded-xl text-base text-red-400 bg-red-500/10"
            >
              <LogOut size={20} /> Sair
            </button>
          </nav>
        </div>
      )}

      <main className="flex-1 lg:ml-0 pt-16 lg:pt-0 min-w-0">
        {children}
      </main>
    </div>
  );
}
